import { createApi } from '@reduxjs/toolkit/query/react';
import type { MediaPlanFlatten } from '@skyrise-eng/types';

export interface MediaPlanListResponse {
  data: MediaPlanFlatten[];
  count: number;
  dealsWithoutMediaPlans?: number;
}
import { baseQuery } from './baseQuery';
import { buildQueryString } from '@/utils';
import { TAG } from '@/constants';
import { normalizeMediaPlanResponse } from './mediaPlanMappers';
import {
  CreateMediaPlanRequest,
  UpdateMediaPlanRequest,
  GetMediaPlanListQuery,
  MediaPlanDetails
} from '@/models';

const MEDIA_PLAN_URL = 'media-plans/api/v1';

export interface TradingDealPercentResponse {
  tradingDealPercent: number;
}

// Define a service using a base URL and expected endpoints
export const mediaPlanApi = createApi({
  reducerPath: 'mediaPlanApi',
  tagTypes: [TAG.MEDIA_PLAN, TAG.CHANNEL],
  baseQuery,
  endpoints: (builder) => ({
    getMediaPlanList: builder.query<MediaPlanListResponse, GetMediaPlanListQuery>({
      query: (params) => `${MEDIA_PLAN_URL}?${buildQueryString(params)}`,
      transformResponse: (response: MediaPlanListResponse) => ({
        ...response,
        data: response.data.map((plan) => normalizeMediaPlanResponse(plan as MediaPlanDetails))
      }),
      providesTags: () => [TAG.MEDIA_PLAN]
    }),
    getTradingDealPercent: builder.query<TradingDealPercentResponse, string>({
      query: (clientId) => `${MEDIA_PLAN_URL}/trading-deal-percent?clientId=${encodeURIComponent(clientId)}`,
      providesTags: () => [TAG.MEDIA_PLAN]
    }),
    createMediaPlan: builder.mutation<MediaPlanFlatten, CreateMediaPlanRequest>({
      query: (mediaPlan) => ({
        url: MEDIA_PLAN_URL,
        method: 'POST',
        body: mediaPlan
      }),
      transformResponse: (response: MediaPlanDetails) => normalizeMediaPlanResponse(response),
      invalidatesTags: [TAG.MEDIA_PLAN, TAG.CHANNEL]
    }),
    updateMediaPlan: builder.mutation<MediaPlanFlatten, UpdateMediaPlanRequest>({
      query: ({ _id, ...rest }) => ({
        url: `${MEDIA_PLAN_URL}/${_id}`,
        method: 'PUT',
        body: rest
      }),
      transformResponse: (response: MediaPlanDetails) => normalizeMediaPlanResponse(response),
      invalidatesTags: [TAG.MEDIA_PLAN, TAG.CHANNEL]
    }),
    deleteMediaPlan: builder.mutation<MediaPlanFlatten, string>({
      query: (id) => ({
        url: `${MEDIA_PLAN_URL}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.MEDIA_PLAN]
    }),
    getMediaPlan: builder.query<MediaPlanDetails, string>({
      query: (id) => `${MEDIA_PLAN_URL}/${id}`,
      transformResponse: (response: MediaPlanDetails) => normalizeMediaPlanResponse(response),
      providesTags: [TAG.MEDIA_PLAN]
    }),
    /**
     * Renders the IO for a media plan from NRVE's own template. Returns a PDF
     * blob; no PandaDoc document is created, so this works before an IO is
     * raised and reflects the current template settings.
     */
    previewIo: builder.mutation<Blob, { id: string; template?: string }>({
      query: ({ id, template }) => ({
        url: `${MEDIA_PLAN_URL}/${id}/io-preview?format=pdf${template ? `&template=${template}` : ''}`,
        responseHandler: (response) => response.blob()
      })
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  usePreviewIoMutation,
  useGetMediaPlanListQuery,
  useGetTradingDealPercentQuery,
  useCreateMediaPlanMutation,
  useUpdateMediaPlanMutation,
  useDeleteMediaPlanMutation,
  useGetMediaPlanQuery,
  util: mediaPlanUtil
} = mediaPlanApi;
